import { useTranslation } from "react-i18next";
import useFormHook from "@shared/hooks/useFormHook";
import { noticePeriodService } from "../services/noticePeriod";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import LoadingButton from '@shared/components/LoadingButton';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';

export default function NoticePeriodForm({ onClose, onConfirm, editData, formProps }) {
    const { t } = useTranslation();
    const validationSchema = yup.object({
        code: yup.string().required(t('common.required')),
        description: yup.string().required(t('common.required')),
    });
    const { modalRef } = useFormHook({}, onClose);
    const { register, handleSubmit, setValue, reset, formState: { errors } } =
        useForm({
            resolver: yupResolver(validationSchema),
            defaultValues: {
                active: true,
                months: '0',
                days: '0'
            },
            ...formProps
        });
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(false);

    // Fetch data if editing
    useEffect(() => {
        const fetchNoticePeriodData = async () => {
            if (editData && editData.id) {
                try {
                    setFetchingData(true);
                    const data = await noticePeriodService.getNoticePeriodById(editData.id);
                    
                    setValue('code', data.code || '');
                    setValue('description', data.description || '');
                    setValue('months', data.months !== undefined && data.months !== null ? data.months.toString() : '0');
                    setValue('days', data.days !== undefined && data.days !== null ? data.days.toString() : '0');
                    setValue('active', data.active !== undefined ? data.active : true);
                } catch (error) {
                    console.error('Error fetching notice period data:', error);
                } finally {
                    setFetchingData(false);
                }
            } else {
                reset({
                    active: true,
                    months: '0',
                    days: '0'
                });
            }
        };

        fetchNoticePeriodData();
    }, [editData, setValue, reset]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            const formData = {
                code: data.code,
                description: data.description,
                months: data.months ? parseInt(data.months) : 0,
                days: data.days ? parseInt(data.days) : 0,
                active: data.active
            };
            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')} 
                        </h4>
                    </div>
                    {fetchingData ? (
                        <div className="modal-body">
                            <p>{t('common.loading') || 'Loading...'}</p>
                        </div>
                    ) : (
                        <form id="noticePeriodForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span>{t('staff.noticePeriod.table.headers.code') || 'Code'}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={20}
                                            className={`form-control ${errors.code ? 'input-error' : ''}`}
                                            placeholder={errors.code ? errors.code.message : t('common.required')}
                                            {...register('code')} />
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.noticePeriod.table.headers.description') || 'Description'}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={100}
                                            className={`form-control ${errors.description ? 'input-error' : ''}`}
                                            placeholder={errors.description ? errors.description.message : t('common.required')}
                                            {...register('description')} />
                                    </div>
                                </div>
                                <div className="form-section">
                                    <div className="form-group">
                                        <span>{t('staff.noticePeriod.table.headers.months') || 'Months'}</span>
                                        <input 
                                            type="text" 
                                            maxLength={2} 
                                            className={`form-control ${errors.months ? 'input-error' : ''}`}
                                            onInput={(e) => {
                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                            }}
                                            {...register('months')} 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.noticePeriod.table.headers.days') || 'Days'}</span>
                                        <input 
                                            type="text" 
                                            maxLength={2} 
                                            className={`form-control ${errors.days ? 'input-error' : ''}`}
                                            onInput={(e) => {
                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                            }}
                                            {...register('days')} 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <div className="checkbox-wrapper-start title-checkbox">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                {...register('active')}
                                            />
                                            <label htmlFor="active">{t('staff.noticePeriod.table.headers.active') || 'Active'}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="noticePeriodForm" className="btn btn-primary" loading={loading} disabled={fetchingData}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

